import { useEffect, useState } from "react";
import { BarChart3, ExternalLink } from "lucide-react";
import { api } from "@/lib/api";

const CAT_LABELS = { gen: "General", obc: "OBC", ews: "EWS", sc: "SC", st: "ST" };

export default function CutoffAnalyticsTable() {
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    api.get("/cutoffs").then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data) {
    return (
      <section className="bg-white border border-slate-200 rounded-xl p-5 md:p-6" data-testid="cutoff-loading">
        <div className="h-5 w-48 bg-slate-100 rounded mb-3 animate-pulse" />
      </section>
    );
  }

  return (
    <section
      className="bg-white border border-slate-200 rounded-xl p-5 md:p-7 mt-8"
      aria-labelledby="cutoffs-h2"
      data-testid="cutoff-analytics"
    >
      <div className="flex items-center gap-2 mb-1">
        <span className="inline-grid place-items-center w-8 h-8 rounded-md bg-orange-100 text-orange-700">
          <BarChart3 className="w-4 h-4" aria-hidden="true" />
        </span>
        <h2
          id="cutoffs-h2"
          className="text-xl md:text-2xl font-extrabold text-slate-900"
          style={{ fontFamily: "'Cabinet Grotesk',sans-serif" }}
        >
          Previous Year Cut-Off Analytics
        </h2>
      </div>
      <p className="text-sm text-slate-700 mb-5 leading-relaxed">
        Real category-wise qualifying marks for the 10 most-searched Indian govt exams — last 3 cycles. All figures verified from official SSC, IBPS, SBI, UPSC, RRB result PDFs.
      </p>

      <div className="space-y-4">
        {data.exams.map((ex) => {
          const isOpen = expanded === ex.exam_slug;
          return (
            <article key={ex.exam_slug} className="border border-slate-200 rounded-lg overflow-hidden" data-testid={`cutoff-card-${ex.exam_slug}`}>
              <button
                type="button"
                onClick={() => setExpanded(isOpen ? null : ex.exam_slug)}
                className="w-full flex items-center justify-between gap-3 px-4 py-3 bg-slate-50 hover:bg-slate-100 transition-colors text-left"
                aria-expanded={isOpen}
              >
                <div>
                  <div className="font-bold text-slate-900 text-sm md:text-base" style={{ fontFamily: "'Cabinet Grotesk',sans-serif" }}>
                    {ex.exam}
                  </div>
                  <div className="text-[11px] text-slate-600">Out of {ex.out_of} marks · click to see year-wise cut-off</div>
                </div>
                <span className="text-xs font-bold text-orange-600 whitespace-nowrap">
                  {isOpen ? "Hide ▴" : "Show ▾"}
                </span>
              </button>
              {isOpen && (
                <div className="overflow-x-auto bg-white">
                  <table className="w-full text-xs md:text-sm border-collapse">
                    <thead>
                      <tr className="bg-slate-100">
                        <th scope="col" className="text-left px-3 py-2 font-bold text-slate-700 border-b border-slate-200">Year</th>
                        {data.categories.map((c) => (
                          <th key={c} scope="col" className="text-left px-3 py-2 font-bold text-slate-700 border-b border-slate-200">{CAT_LABELS[c]}</th>
                        ))}
                        <th scope="col" className="text-left px-3 py-2 font-bold text-slate-700 border-b border-slate-200 hidden md:table-cell">Notes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ex.rows.map((r) => (
                        <tr key={r.year} className="even:bg-slate-50">
                          <th scope="row" className="px-3 py-2 font-bold text-slate-900 border-b border-slate-100 align-top">{r.year}</th>
                          {data.categories.map((c) => (
                            <td key={c} className="px-3 py-2 text-slate-700 border-b border-slate-100 align-top whitespace-nowrap">{r[c]}</td>
                          ))}
                          <td className="px-3 py-2 text-slate-600 border-b border-slate-100 align-top hidden md:table-cell">{r.notes}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {ex.source && (
                    <div className="px-3 py-2 text-[11px] text-slate-500 border-t border-slate-100 bg-slate-50">
                      Source:{" "}
                      <a href={ex.source} target="_blank" rel="noopener noreferrer" className="text-orange-600 hover:underline inline-flex items-center gap-1">
                        {ex.source.replace(/^https?:\/\//, "")} <ExternalLink className="w-3 h-3" />
                      </a>
                    </div>
                  )}
                </div>
              )}
            </article>
          );
        })}
      </div>

      <p className="mt-4 text-[11px] text-slate-500 leading-relaxed">
        Headline cut-offs for the most common post in each exam. Cut-offs vary by region and post preference — always cross-check the official result PDF for your exact post code.
      </p>
    </section>
  );
}
